import React, { useState, useCallback, useId, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { getApiBaseUrl } from '../lib/apiBaseUrl';
import './AuthPage.css';

const API_BASE_URL = getApiBaseUrl();
const PUB = process.env.PUBLIC_URL || '';
const GOOGLE_CLIENT_ID = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
const IS_DEV = process.env.NODE_ENV === 'development';

/**
 * @param {unknown} err
 * @returns {string}
 */
function getRequestErrorMessage(err) {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (data && typeof data === 'object') {
      if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
      if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
    }
    if (!err.response && err.message === 'Network Error') {
      return 'Нет связи с сервером. Запустите backend (папка server, тот же порт, что и Colyseus).';
    }
    if (err.response?.status === 401) {
      return 'Неверное имя пользователя или пароль.';
    }
    if (err.response?.status === 409) {
      const d = err.response?.data;
      return typeof d?.error === 'string' ? d.error : 'Такой аккаунт уже существует.';
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Произошла ошибка. Попробуйте ещё раз.';
}

function AuthPage({ onLogin }) {
  const formTitleId = useId();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const googleWrapRef = useRef(null);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(320);

  useLayoutEffect(() => {
    if (!GOOGLE_CLIENT_ID) return undefined;
    const el = googleWrapRef.current;
    if (!el) return undefined;

    const update = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      if (!Number.isFinite(w) || w < 1) return;
      setGoogleBtnWidth(Math.min(360, Math.max(240, w)));
    };

    update();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  }, []);

  const validateRegister = useCallback(() => {
    const u = formData.username.trim();
    const email = formData.email.trim();
    if (u.length < 3 || u.length > 32) {
      setError('Имя пользователя: от 3 до 32 символов.');
      return false;
    }
    if (!/^[a-zA-Z0-9_\-\u0400-\u04FF]+$/.test(u)) {
      setError('В имени допустимы буквы, цифры, символы _ и -');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Введите корректный email.');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Пароль не короче 6 символов.');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Пароли не совпадают.');
      return false;
    }
    return true;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!isLogin && !validateRegister()) {
      setLoading(false);
      return;
    }

    if (isLogin) {
      const u = formData.username.trim();
      if (!u) {
        setError('Введите имя пользователя.');
        setLoading(false);
        return;
      }
      if (formData.password.length < 1) {
        setError('Введите пароль.');
        setLoading(false);
        return;
      }
    }

    try {
      if (isLogin) {
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/login`,
          {
            username: formData.username.trim(),
            password: formData.password,
          },
          { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
        );

        onLogin(response.data.user, response.data.token, { isNewAccount: false });
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/register`,
          {
            username: formData.username.trim(),
            email: formData.email.trim(),
            password: formData.password,
          },
          { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
        );

        onLogin(response.data.user, response.data.token, { isNewAccount: true });
      }
    } catch (err) {
      setError(getRequestErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const setMode = (login) => {
    setIsLogin(login);
    setError('');
    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
  };

  const handleGoogleSuccess = useCallback(
    async (credentialResponse) => {
      const cred = credentialResponse?.credential;
      if (!cred) {
        setError('Нет данных от Google.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/google`,
          { credential: cred },
          { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
        );
        onLogin(response.data.user, response.data.token, {
          isNewAccount: Boolean(response.data.created),
        });
      } catch (err) {
        setError(getRequestErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [onLogin]
  );

  const errorId = 'auth-form-error';

  return (
    <div className="auth-page">
      <aside className="auth-hero" aria-hidden>
        <div className="auth-hero__bg" style={{ backgroundImage: `url(${PUB}/background-for-online.png)` }} />
        <div className="auth-hero__veil" />
        <div className="auth-hero__content">
          <p className="auth-hero__eyebrow">Онлайн-карты</p>
          <h2 className="auth-hero__title">
            Сека, покер
            <span className="auth-hero__title-break">и белот в одном лобби</span>
          </h2>
          <p className="auth-hero__lead">Войдите и садитесь за стол — прогресс и баланс привязаны к вашему аккаунту.</p>

          <div className="auth-hero__floats">
            <div className="auth-float auth-float--a">
              <img src={`${PUB}/logotip-seka-button.png`} alt="" width={120} height={120} draggable={false} />
            </div>
            <div className="auth-float auth-float--b">
              <img src={`${PUB}/logotip-poker-button.png`} alt="" width={110} height={110} draggable={false} />
            </div>
            <div className="auth-float auth-float--c">
              <img src={`${PUB}/belot-button.png`} alt="" width={100} height={100} draggable={false} />
            </div>
          </div>

          <div className="auth-hero__badge">
            <img src={`${PUB}/fiski.png`} alt="" className="auth-hero__badge-icon" width={36} height={36} draggable={false} />
            <div>
              <strong>10 000</strong>
              <span>фишек на старте после регистрации</span>
            </div>
          </div>

          <div className="auth-hero__mascot">
            <img src={`${PUB}/avatar-for-bot.png`} alt="" width={72} height={72} draggable={false} />
            <span>Увидимся за столом</span>
          </div>
        </div>
      </aside>

      <main className="auth-main">
        <div className="auth-panel">
          <header className="auth-panel__head">
            <div className="auth-brand">
              <img
                className="auth-brand__mark"
                src={`${PUB}/logotip-seka-button.png`}
                alt=""
                width={56}
                height={56}
                draggable={false}
              />
              <div className="auth-brand__text">
                <span className="auth-brand__name">Сека</span>
                <span className="auth-brand__tag">онлайн</span>
              </div>
            </div>
            <p className="auth-panel__tagline" id={formTitleId}>
              {isLogin ? 'С возвращением' : 'Создайте аккаунт за минуту'}
            </p>
          </header>

          <div className="auth-segment" role="tablist" aria-label="Режим входа">
            <button
              type="button"
              role="tab"
              aria-selected={isLogin}
              className={`auth-segment__btn${isLogin ? ' is-active' : ''}`}
              onClick={() => setMode(true)}
            >
              Вход
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isLogin}
              className={`auth-segment__btn${!isLogin ? ' is-active' : ''}`}
              onClick={() => setMode(false)}
            >
              Регистрация
            </button>
          </div>

          {GOOGLE_CLIENT_ID ? (
            <>
              <div
                ref={googleWrapRef}
                className={`auth-google${loading ? ' is-busy' : ''}`}
              >
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Не удалось войти через Google. Попробуйте ещё раз.')}
                  theme="filled_black"
                  size="large"
                  width={googleBtnWidth}
                  text="continue_with"
                  locale="ru"
                />
              </div>
              <p className="auth-oauth-divider" role="presentation">
                <span>или</span>
              </p>
            </>
          ) : IS_DEV ? (
            <div className="auth-google-hint" role="note">
              <strong>Google-вход не настроен во фронте.</strong>
              <p>
                Добавьте <code className="auth-google-hint__code">REACT_APP_GOOGLE_CLIENT_ID=…</code> (тот же Web Client ID, что{' '}
                <code className="auth-google-hint__code">GOOGLE_CLIENT_ID</code> в <code className="auth-google-hint__code">server/.env</code>) в{' '}
                <code className="auth-google-hint__code">client/.env.development.local</code> или в{' '}
                <code className="auth-google-hint__code">client/.env.local</code> — его не трогает скрипт запуска.
                Сохраните файл и полностью перезапустите <code className="auth-google-hint__code">npm start</code>.
              </p>
              <p className="auth-google-hint__sub">
                Если пользуетесь <code className="auth-google-hint__code">scripts/start-dev-online.ps1</code>: раньше он затирал{' '}
                <code className="auth-google-hint__code">.env.development.local</code> — обновите репозиторий до версии скрипта, которая сохраняет
                остальные строки, и снова пропишите <code className="auth-google-hint__code">REACT_APP_GOOGLE_CLIENT_ID</code>.
              </p>
            </div>
          ) : null}

          <form
            onSubmit={handleSubmit}
            className="auth-form"
            aria-labelledby={formTitleId}
            aria-describedby={error ? errorId : undefined}
            noValidate
          >
            <div className="auth-form__fields">
              <div className="form-group">
                <label htmlFor="username">Имя пользователя</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder={isLogin ? 'Ваш ник в игре' : 'От 3 до 32 символов'}
                  required
                  minLength={isLogin ? 1 : 3}
                  maxLength={32}
                />
              </div>

              {!isLogin && (
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="password">Пароль</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isLogin ? '••••••••' : 'Не короче 6 символов'}
                  required
                  minLength={isLogin ? 1 : 6}
                />
              </div>

              {!isLogin && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Подтвердите пароль</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Повторите пароль"
                    required
                    minLength={6}
                  />
                </div>
              )}
            </div>

            {error ? (
              <div className="auth-error" id={errorId} role="alert">
                {error}
              </div>
            ) : null}

            <button type="submit" className="auth-submit" disabled={loading} aria-busy={loading}>
              {loading ? (
                <span className="auth-submit__spinner" aria-hidden />
              ) : null}
              <span>{loading ? 'Подождите…' : isLogin ? 'Войти в лобби' : 'Зарегистрироваться'}</span>
            </button>
          </form>

          <section className="auth-tips" aria-label="Подсказки">
            <h3 className="auth-tips__title">Полезно знать</h3>
            <ul className="auth-tips__list">
              <li>Один аккаунт — лобби и столы; не заходите с двух устройств за один стол.</li>
              <li>В dev (`npm start`) вход без API возможен — см. настройки приложения.</li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  );
}

export default AuthPage;
